import { z } from 'zod';
import { idSchema, unitKeySchema } from './common.js';
import { ingredientCategorySchema } from './ingredients.js';
/** Stores, products, per-store shelf-life rules, locations and prices — Phase 3 (PLAN §4.4–4.5, D5/D6/D12). */
export const DEPARTMENTS = [
    'produce',
    'deli',
    'bakery',
    'butcher',
    'seafood',
    'aisles',
    'dairy',
    'frozen',
    'other',
];
export const departmentSchema = z.enum(DEPARTMENTS);
export const LOCATION_TYPES = ['aisle', 'endcap', 'department'];
export const locationTypeSchema = z.enum(LOCATION_TYPES);
/** "aisle 12", "endcap 3", "department produce" */
export const storeLocationSchema = z.object({
    type: locationTypeSchema,
    value: z.string().trim().min(1).max(60),
    /** Department the location sits in (drives sorting); inferred from `value` when absent. */
    department: departmentSchema.optional(),
});
// ------------------------------------------------------------------ stores --
export const storeDtoSchema = z.object({
    id: idSchema,
    chain: z.string(),
    name: z.string(),
    address: z.string().nullable(),
    lat: z.number(),
    lng: z.number(),
    radiusM: z.number().int().positive(),
    /** Household's department order for this store (defaults to DEPARTMENTS). */
    departmentOrder: z.array(departmentSchema),
    /** Household marked this store as one they shop at. */
    favourite: z.boolean(),
    /** Household's whole-store shelf-life adjustment, if any (D6). */
    shelfLifeAdjustDays: z.number().int().nullable(),
    shelfLifeAdjustPct: z.number().nullable(),
    createdAt: z.iso.datetime(),
});
export const createStoreRequestSchema = z.object({
    chain: z.string().trim().min(1).max(80),
    name: z.string().trim().min(1).max(120),
    address: z.string().trim().max(300).nullable().optional(),
    lat: z.number().min(-90).max(90),
    lng: z.number().min(-180).max(180),
    radiusM: z.number().int().min(25).max(2000).optional(),
});
export const updateStoreRequestSchema = createStoreRequestSchema.partial();
/** GET /api/v1/stores?q=&near=lat,lng — shared table; `near` sorts by distance */
export const storeListQuerySchema = z.object({
    q: z.string().trim().max(120).optional(),
    near: z
        .string()
        .regex(/^-?\d+(\.\d+)?,-?\d+(\.\d+)?$/)
        .optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
});
export const storeListResponseSchema = z.object({
    stores: z.array(storeDtoSchema.extend({ distanceM: z.number().nullable() })),
});
/** GET /api/v1/stores/nearest?lat=&lng= (PLAN §4.7) */
export const nearestStoreQuerySchema = z.object({
    lat: z.coerce.number().min(-90).max(90),
    lng: z.coerce.number().min(-180).max(180),
});
export const nearestStoreResponseSchema = z.object({
    /** Stores within their own radius of the point, nearest first. */
    matches: z.array(storeDtoSchema.extend({ distanceM: z.number() })),
});
/**
 * PUT /api/v1/stores/:id/prefs — household-scoped. `departmentOrder` may be
 * partial; the API appends any departments left out, in canonical order.
 * The shelf-life fields write the whole-store adjustment (category = null).
 */
export const updateStorePrefsRequestSchema = z.object({
    departmentOrder: z.array(departmentSchema).min(1).optional(),
    favourite: z.boolean().optional(),
    shelfLifeAdjustDays: z.number().int().min(-60).max(60).nullable().optional(),
    shelfLifeAdjustPct: z.number().min(-90).max(200).nullable().optional(),
    note: z.string().trim().max(300).nullable().optional(),
});
// -------------------------------------------------- shelf-life rules (D6) --
export const shelfLifeAdjustmentDtoSchema = z.object({
    id: idSchema,
    storeId: idSchema,
    ingredientCategory: ingredientCategorySchema.nullable(),
    adjustDays: z.number().int().nullable(),
    adjustPct: z.number().nullable(),
    note: z.string().nullable(),
});
export const upsertShelfLifeAdjustmentRequestSchema = z.object({
    ingredientCategory: ingredientCategorySchema.nullable(),
    adjustDays: z.number().int().min(-60).max(60).nullable().optional(),
    adjustPct: z.number().min(-90).max(200).nullable().optional(),
    note: z.string().trim().max(300).nullable().optional(),
});
export const storeProductShelfLifeDtoSchema = z.object({
    storeId: idSchema,
    productId: idSchema,
    /** Product display label ("2% milk, 1 gal") so lists render without a second lookup. */
    label: z.string(),
    days: z.number().int().nonnegative(),
    note: z.string().nullable(),
});
/** GET /api/v1/stores/:id/shelf-life — the household's rules for this store (D6). */
export const shelfLifeAdjustmentsResponseSchema = z.object({
    adjustments: z.array(shelfLifeAdjustmentDtoSchema),
    products: z.array(storeProductShelfLifeDtoSchema),
});
/** PUT /api/v1/stores/:id/shelf-life/product (remove via DELETE /:id/shelf-life/product/:productId) */
export const upsertStoreProductShelfLifeRequestSchema = z.object({
    productId: idSchema,
    days: z.number().int().min(0).max(3650),
    note: z.string().trim().max(300).nullable().optional(),
});
// ---------------------------------------------------------------- products --
export const productDtoSchema = z.object({
    id: idSchema,
    ingredientId: idSchema,
    ingredientName: z.string(),
    packageQty: z.number().positive(),
    packageUnit: unitKeySchema,
    brand: z.string().nullable(),
    upc: z.string().nullable(),
    /** "2% milk, 1 gal" */
    label: z.string(),
});
export const createProductRequestSchema = z.object({
    ingredientId: idSchema,
    packageQty: z.number().positive().max(100000),
    packageUnit: unitKeySchema,
    brand: z.string().trim().max(120).nullable().optional(),
    upc: z
        .string()
        .trim()
        .regex(/^\d{8,14}$/)
        .nullable()
        .optional(),
    label: z.string().trim().min(1).max(200).optional(),
});
export const productListQuerySchema = z.object({
    ingredientId: idSchema.optional(),
    upc: z.string().trim().max(14).optional(),
    q: z.string().trim().max(120).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
});
export const productListResponseSchema = z.object({ products: z.array(productDtoSchema) });
/**
 * PUT /api/v1/stores/products/default — the household's default product for one
 * ingredient (used to round list quantities up to whole packages, §4.3).
 * `storeId` scopes it to one store; omitted or null means "any store" (the
 * fallback used where no per-store default exists). Check-offs also learn
 * these. `productId: null` clears it.
 */
export const setDefaultProductRequestSchema = z.object({
    ingredientId: idSchema,
    storeId: idSchema.nullable().optional(),
    productId: idSchema.nullable(),
});
export const defaultProductDtoSchema = z.object({
    ingredientId: idSchema,
    /** Null for the household's any-store default. */
    storeId: idSchema.nullable(),
    storeName: z.string().nullable(),
    product: productDtoSchema,
});
/** GET /api/v1/stores/products/defaults */
export const defaultProductsResponseSchema = z.object({
    defaults: z.array(defaultProductDtoSchema),
});
// --------------------------------------- store×product: location + price --
/** What a suggested location is based on (PLAN §4.5): sync with `SUGGESTION_SOURCES` in core. */
export const SUGGESTION_SOURCES = ['same_chain', 'department', 'category'];
export const suggestionSourceSchema = z.enum(SUGGESTION_SOURCES);
/** Aggregated, never attributed (D12); excludes disabled households (D13). */
export const storeProductInfoSchema = z.object({
    storeId: idSchema,
    productId: idSchema,
    location: storeLocationSchema.nullable(),
    /**
     * Median of recent price observations in cents: one currency, at most two per
     * household, MAD outliers dropped (core `aggregatePrice`).
     */
    priceCents: z.number().int().nonnegative().nullable(),
    currency: z.string().default('USD'),
    priceObservations: z.number().int().nonnegative(),
    lastObservedAt: z.iso.datetime().nullable(),
    /** When `location` is null: a greyed suggestion with its provenance (PLAN §4.5). */
    suggestedLocation: storeLocationSchema.extend({ source: suggestionSourceSchema }).nullable(),
});
export const storeProductInfoResponseSchema = z.object({ info: storeProductInfoSchema });
export const storeProductInfoBatchQuerySchema = z.object({
    productIds: z.string().min(1), // comma-separated ids
});
export const storeProductInfoBatchResponseSchema = z.object({
    infos: z.array(storeProductInfoSchema),
});
/** POST /api/v1/stores/:storeId/products/:productId/observations — append-only */
export const recordObservationRequestSchema = z
    .object({
    location: storeLocationSchema.optional(),
    priceCents: z.number().int().min(0).max(100000000).optional(),
    currency: z.string().length(3).default('USD'),
})
    .refine((o) => o.location || o.priceCents !== undefined, 'location or price required');
/**
 * GET …/price-history — up to 60 recent prices, oldest first. `observedAt` is
 * coarsened to the UTC date (no time of day) so the history cannot be used to
 * tell when a particular household shops (PLAN §9 #3).
 */
export const priceHistoryResponseSchema = z.object({
    points: z.array(z.object({ observedAt: z.iso.date(), priceCents: z.number().int() })),
});
//# sourceMappingURL=stores.js.map